const reviewSnapshot = new Map();

function stripMoreText(value) {
  return (value || '')
    .replace(/[“”]/g, '')
    .replace(/\s*More\s*$/i, '')
    .trim();
}

function reviewBodyFromEl(el) {
  const body = el.querySelector(
    '.jdgm-rev__body, .jm-review-content__body, .jdgm-review-content__body-content'
  );
  if (!body) return '';

  const clone = body.cloneNode(true);
  clone
    .querySelectorAll('button, .jm-review-content__expand-button, .jdgm-visually-hidden')
    .forEach((node) => node.remove());

  return stripMoreText(clone.textContent);
}

function snapshotReviewsFrom(root = document) {
  const scope =
    root === document
      ? document.querySelector(
          '#judgeme_product_reviews, .jdgm-ssr-reviews, .jdgm-rev-widg, .jdgm-review-widget'
        ) || document
      : root;

  scope.querySelectorAll('.jdgm-rev[data-review-id]').forEach((el) => {
    const id = el.getAttribute('data-review-id');
    const body = reviewBodyFromEl(el);
    if (!id || !body) return;

    const existing = reviewSnapshot.get(id);
    if (existing && existing.body.length >= body.length) return;

    reviewSnapshot.set(id, {
      id,
      body,
      author: el.querySelector('.jdgm-rev__author, .jm-reviewer-name')?.textContent?.trim() || '',
      title: el.querySelector('.jdgm-rev__title, .jm-review-content__title')?.textContent?.trim() || '',
      stars: el.querySelector('.jdgm-rev__rating, .jm-star-rating')?.cloneNode(true) || null,
    });
  });
}

function reviewsRoot() {
  return (
    document.querySelector('#judgeme_product_reviews') ||
    document.querySelector('.jdgm-rev-widg') ||
    document.querySelector('.jdgm-review-widget') ||
    document.querySelector('.jdgm-ssr-reviews') ||
    document.body
  );
}

snapshotReviewsFrom();

if (!customElements.get('review-highlights-drawer')) {
  class ReviewHighlightsDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('dialog');
      this.bodyEl = this.querySelector('[data-review-highlights-body]');
      this.trigger = this.querySelector('[data-review-highlights-trigger]');
      this.closeButtons = this.querySelectorAll('[data-review-highlights-close]');
      this.reviewsLink = this.querySelector('[data-review-highlights-reviews]');
      this.supportsTopLayerAnimation =
        window.CSS && CSS.supports('transition-behavior', 'allow-discrete');
      this.supportsClosedBy = this.dialog && 'closedBy' in this.dialog;

      if (!this.dialog || !this.bodyEl) return;

      snapshotReviewsFrom();
      this.placeTrigger();

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      if (!this.supportsClosedBy) {
        this.dialog.addEventListener('click', (event) => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          const inPanel =
            rect.top <= event.clientY &&
            event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX &&
            event.clientX <= rect.left + rect.width;
          if (!inPanel) this.close();
        });
      }

      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-open');
        document.documentElement.classList.remove('review-highlights-drawer-open');
        this.trigger?.setAttribute('aria-expanded', 'false');
      });

      this.reviewsLink?.addEventListener('click', (event) => {
        const target = document.querySelector('#judgeme_product_reviews, .jdgm-rev-widg, .jdgm-review-widget');
        if (!target) return;
        event.preventDefault();
        this.close();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.trigger?.addEventListener('click', () => this.open());

      this.insightsObserver = new MutationObserver(() => {
        window.clearTimeout(this.attachTimer);
        this.attachTimer = window.setTimeout(() => this.attachInsights(), 150);
      });
      this.attachInsights();
      this.observeInsights();
      this.attachTimerMax = window.setTimeout(() => this.stopObserving(), 8000);
    }

    disconnectedCallback() {
      window.clearTimeout(this.attachTimer);
      window.clearTimeout(this.attachTimerMax);
      this.insightsObserver?.disconnect();
    }

    observeInsights() {
      const observer = this.insightsObserver;
      if (!observer || typeof observer.observe !== 'function' || this.insightsReady) return;

      const root = reviewsRoot();
      if (!root) return;

      observer.disconnect();
      observer.observe(root, { childList: true, subtree: true });
    }

    stopObserving() {
      this.insightsReady = true;
      window.clearTimeout(this.attachTimer);
      window.clearTimeout(this.attachTimerMax);
      this.insightsObserver?.disconnect();
    }

    attachInsights() {
      if (!this.isConnected || !this.bodyEl || this.insightsReady) return;

      snapshotReviewsFrom();

      const scope = reviewsRoot();
      const liveInsights = Array.from(scope.querySelectorAll('.jm-ai-insights')).find(
        (node) => !this.bodyEl.contains(node) && node.textContent.trim()
      );
      const ssr = Array.from(scope.querySelectorAll('.jdgm-ssr-ai-content')).find(
        (node) => !this.bodyEl.contains(node)
      );

      const hasFullReviews = Boolean(this.bodyEl.querySelector('.review-highlights-full__item:not([data-quote])'));
      const hasLive = Boolean(this.bodyEl.querySelector('.jm-ai-insights'));
      const hasSsr = Boolean(this.bodyEl.querySelector('.jdgm-ssr-ai-content'));
      const source = liveInsights && !hasLive ? liveInsights : !hasLive && !hasSsr ? ssr : null;

      if (source && !hasFullReviews) {
        const clone = source.cloneNode(true);
        clone.querySelector('.jdgm-ssr-rating-distribution')?.remove();
        this.expandHighlights(clone);
        this.bodyEl.replaceChildren(clone);
      } else if (hasLive || hasSsr || hasFullReviews) {
        this.expandHighlights(this.bodyEl);
      }

      if (source || hasLive || hasSsr || hasFullReviews) {
        this.showTrigger();
        this.stopObserving();
      }
    }

    highlightEntries() {
      const widgets = window.jdgm?.data?.reviewWidget || {};
      for (const widget of Object.values(widgets)) {
        const translations = widget.ai_summary_translations || {};
        const entry = translations.en || Object.values(translations)[0];
        const highlights = (entry?.highlights || []).filter((item) => item?.highlight);
        if (highlights.length) return highlights;
      }
      return [];
    }

    normalizeText(value) {
      return stripMoreText(value)
        .toLowerCase()
        .replace(/[']/g, '')
        .replace(/\s+/g, ' ');
    }

    collectReviews() {
      const scope = reviewsRoot();
      const fromDom = Array.from(scope.querySelectorAll('.jdgm-rev'))
        .filter((el) => !this.bodyEl.contains(el))
        .map((el) => ({
          id: el.getAttribute('data-review-id'),
          el,
          body: reviewBodyFromEl(el),
          author: el.querySelector('.jdgm-rev__author, .jm-reviewer-name')?.textContent?.trim() || '',
          title: el.querySelector('.jdgm-rev__title, .jm-review-content__title')?.textContent?.trim() || '',
          stars: el.querySelector('.jdgm-rev__rating, .jm-star-rating'),
        }))
        .filter((review) => review.body);

      fromDom.forEach((review) => {
        if (review.id) reviewSnapshot.set(review.id, { ...review, stars: review.stars?.cloneNode(true) || null });
      });

      const reviews = [...fromDom];
      reviewSnapshot.forEach((review, id) => {
        if (!reviews.some((item) => item.id === id)) reviews.push(review);
      });
      return reviews;
    }

    findReview(reviews, quote, used, uuid) {
      if (uuid) {
        const byId = reviews.find((review) => !used.has(review.id || review.el) && review.id === uuid);
        if (byId) return byId;
      }

      const normalizedQuote = this.normalizeText(quote);
      if (!normalizedQuote) return null;

      const sentences = normalizedQuote
        .split(/[.!?]+/)
        .map((part) => part.trim())
        .filter((part) => part.length > 8);

      return (
        reviews.find((review) => {
          const key = review.id || review.el;
          if (used.has(key)) return false;
          const body = this.normalizeText(review.body);
          if (body.includes(normalizedQuote)) return true;
          return sentences.length > 0 && sentences.every((sentence) => body.includes(sentence));
        }) || null
      );
    }

    buildReviewCard(review, quote) {
      const article = document.createElement('article');
      article.className = 'review-highlights-full__item';

      const meta = document.createElement('div');
      meta.className = 'review-highlights-full__meta';

      if (review?.stars) {
        const stars = document.createElement('span');
        stars.className = 'review-highlights-full__stars';
        stars.append(review.stars.cloneNode(true));
        meta.append(stars);
      }

      if (review?.author) {
        const author = document.createElement('span');
        author.className = 'review-highlights-full__author';
        author.textContent = review.author;
        meta.append(author);
      }

      if (meta.childElementCount) article.append(meta);

      if (review?.title) {
        const title = document.createElement('p');
        title.className = 'review-highlights-full__title';
        title.textContent = stripMoreText(review.title);
        article.append(title);
      }

      const body = document.createElement('p');
      body.className = 'review-highlights-full__body';
      body.textContent = stripMoreText(review?.body || quote);
      article.append(body);

      if (!review) article.dataset.quote = stripMoreText(quote);

      return article;
    }

    highlightNodes(root) {
      let nodes = root.querySelectorAll('.jdgm-ssr-review-highlights li, .jm-review-highlights__row');
      if (!nodes.length) nodes = root.querySelectorAll('.jm-review-highlights__text');
      return nodes;
    }

    expandHighlights(root) {
      snapshotReviewsFrom();
      const reviews = this.collectReviews();
      const used = new Set();
      const entries = this.highlightEntries();

      const existingList = root.querySelector('.review-highlights-full');
      if (existingList) {
        if (!existingList.querySelector('.review-highlights-full__heading')) {
          const heading = document.createElement('h3');
          heading.className = 'review-highlights-full__heading';
          heading.textContent = 'Review Highlights';
          existingList.prepend(heading);
        }

        existingList.querySelectorAll('.review-highlights-full__item:not([data-quote])').forEach((card) => {
          const body = this.normalizeText(card.querySelector('.review-highlights-full__body')?.textContent);
          const match = reviews.find((review) => this.normalizeText(review.body) === body);
          if (match) used.add(match.id || match.el);
        });

        existingList.querySelectorAll('.review-highlights-full__item[data-quote]').forEach((card, index) => {
          const match = this.findReview(
            reviews,
            card.dataset.quote,
            used,
            entries[index]?.review_uuid
          );
          if (!match) return;
          used.add(match.id || match.el);
          card.replaceWith(this.buildReviewCard(match));
        });
        this.arrangeInsights(root);
        return;
      }

      const nodes = this.highlightNodes(root);
      const quotes = entries.length
        ? entries
        : Array.from(nodes).map((node) => ({
            highlight: stripMoreText(node.querySelector('.jm-review-highlights__text')?.textContent || node.textContent),
            review_uuid: null,
          }));

      if (!quotes.length) return;

      const list = document.createElement('div');
      list.className = 'review-highlights-full';

      const heading = document.createElement('h3');
      heading.className = 'review-highlights-full__heading';
      heading.textContent = 'Review Highlights';
      list.append(heading);

      quotes.forEach((entry) => {
        const quote = stripMoreText(entry.highlight);
        const match = this.findReview(reviews, quote, used, entry.review_uuid);
        if (match) used.add(match.id || match.el);
        list.append(this.buildReviewCard(match, quote));
      });

      const container = root.querySelector('.jdgm-ssr-review-highlights, .jm-review-highlights');
      if (container) {
        container.replaceWith(list);
      } else if (nodes[0]) {
        nodes[0].parentElement?.replaceWith(list);
      } else {
        root.append(list);
      }

      this.arrangeInsights(root);
    }

    arrangeInsights(root) {
      this.ensureDisclaimer(root);
      this.enhanceKeywordChips(root);

      const keywords = root.querySelector('.jdgm-ssr-review-keywords, .jm-keyword-list');
      const highlights = root.querySelector('.review-highlights-full, .jdgm-ssr-review-highlights, .jm-review-highlights');
      if (keywords && highlights) highlights.before(keywords);
    }

    ensureDisclaimer(root) {
      const existing = root.querySelector('.jm-ai-disclaimer, .review-highlights-disclaimer');
      if (existing) {
        this.ensureDisclaimerIcon(existing);
        return;
      }

      const disclaimer = document.createElement('p');
      disclaimer.className = 'jm-ai-disclaimer review-highlights-disclaimer';

      const text = document.createElement('span');
      text.textContent =
        window.jdgmSettings?.widget_ai_summary_disclaimer ||
        'AI-powered review summary based on recent customer reviews';
      disclaimer.append(text);
      this.ensureDisclaimerIcon(disclaimer);

      const summary = root.querySelector('.jm-ai-insights__header, .jdgm-ssr-ai-summary');
      if (summary) {
        summary.before(disclaimer);
      } else {
        root.prepend(disclaimer);
      }
    }

    ensureDisclaimerIcon(el) {
      if (el.querySelector('.jm-ai-disclaimer__icon')) return;

      const icon = document.createElement('span');
      icon.className = 'jm-ai-disclaimer__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12.0025 7.50059C12.0038 7.70445 11.9419 7.9037 11.8252 8.07091C11.7086 8.23813 11.543 8.36509 11.3513 8.43434L8.12754 9.62559L6.94004 12.8518C6.86972 13.0429 6.74249 13.2078 6.57552 13.3242C6.40854 13.4407 6.20987 13.5031 6.00629 13.5031C5.80271 13.5031 5.60404 13.4407 5.43707 13.3242C5.27009 13.2078 5.14286 13.0429 5.07254 12.8518L3.87754 9.62559L0.651291 8.43809C0.460247 8.36776 0.29537 8.24053 0.178907 8.07356C0.0624441 7.90659 0 7.70791 0 7.50434C0 7.30076 0.0624441 7.10208 0.178907 6.93511C0.29537 6.76814 0.460247 6.64091 0.651291 6.57059L3.87754 5.37559L5.06504 2.14934C5.13536 1.95829 5.26259 1.79342 5.42957 1.67695C5.59654 1.56049 5.79522 1.49805 5.99879 1.49805C6.20237 1.49805 6.40104 1.56049 6.56802 1.67695C6.73499 1.79342 6.86222 1.95829 6.93254 2.14934L8.12754 5.37559L11.3538 6.56309C11.5457 6.63296 11.7111 6.76067 11.8274 6.9286C11.9436 7.09653 12.0048 7.29639 12.0025 7.50059Z"/><path fill="currentColor" d="M11 1.5C11.0003 1.55094 10.9848 1.60074 10.9557 1.64252C10.9265 1.68431 10.8851 1.71604 10.8372 1.73334L10.0314 2.03103L9.73463 2.83725C9.71706 2.88499 9.68526 2.92619 9.64352 2.95529C9.60179 2.9844 9.55213 3 9.50125 3C9.45036 3 9.40071 2.9844 9.35897 2.95529C9.31724 2.92619 9.28544 2.88499 9.26786 2.83725L8.96918 2.03103L8.16279 1.73428C8.11504 1.7167 8.07383 1.68491 8.04472 1.64318C8.01561 1.60146 8 1.55181 8 1.50094C8 1.45006 8.01561 1.40042 8.04472 1.35869C8.07383 1.31697 8.11504 1.28517 8.16279 1.2676L8.96918 0.968975L9.26599 0.162754C9.28356 0.115013 9.31536 0.0738112 9.3571 0.0447078C9.39883 0.0156044 9.44849 0 9.49937 0C9.55026 0 9.59991 0.0156044 9.64165 0.0447078C9.68338 0.0738112 9.71518 0.115013 9.73276 0.162754L10.0314 0.968975L10.8378 1.26572C10.8858 1.28318 10.9272 1.3151 10.9562 1.35706C10.9852 1.39903 11.0005 1.44897 11 1.5Z"/><path fill="currentColor" d="M14 4C14.0004 4.06793 13.9798 4.13431 13.9409 4.19003C13.902 4.24574 13.8468 4.28805 13.7829 4.31112L12.7086 4.70803L12.3128 5.78299C12.2894 5.84665 12.247 5.90159 12.1914 5.94039C12.1357 5.97919 12.0695 6 12.0017 6C11.9338 6 11.8676 5.97919 11.812 5.94039C11.7563 5.90159 11.7139 5.84665 11.6905 5.78299L11.2922 4.70803L10.217 4.31237C10.1534 4.28894 10.0984 4.24654 10.0596 4.19091C10.0208 4.13528 10 4.06908 10 4.00125C10 3.93342 10.0208 3.86722 10.0596 3.81159C10.0984 3.75595 10.1534 3.71356 10.217 3.69013L11.2922 3.29197L11.688 2.21701C11.7114 2.15335 11.7538 2.09841 11.8095 2.05961C11.8651 2.02081 11.9313 2 11.9992 2C12.067 2 12.1332 2.02081 12.1889 2.05961C12.2445 2.09841 12.2869 2.15335 12.3103 2.21701L12.7086 3.29197L13.7838 3.68763C13.8477 3.71091 13.9029 3.75347 13.9416 3.80942C13.9803 3.86537 14.0007 3.93196 14 4Z"/></svg>';
      el.prepend(icon);
    }

    enhanceKeywordChips(root) {
      const thumbUp =
        '<svg class="review-highlights-chip__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2 21h4V9H2v12zm20-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L13.17 1 7.59 6.59C7.22 6.95 7 7.45 7 8v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"></path></svg>';
      const thumbDown =
        '<svg class="review-highlights-chip__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M22 3h-4v12h4V3zM2.17 11.12c-.11.25-.17.52-.17.8V14c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L10.83 23l5.58-5.59c.38-.36.59-.86.59-1.41V6c0-1.1-.9-2-2-2H6.16c-.83 0-1.54.5-1.84 1.22l-2.15 5.9z"></path></svg>';

      root.querySelectorAll('.jdgm-ssr-keyword').forEach((chip) => {
        if (chip.querySelector('svg')) return;

        const raw = chip.textContent.trim();
        const match = raw.match(/^(.*?)\s*\((positive|negative|neutral)\)\s*$/i);
        const label = match ? match[1] : raw;
        const sentiment = (match ? match[2] : 'positive').toLowerCase();

        chip.textContent = '';
        chip.dataset.sentiment = sentiment;
        chip.classList.add('review-highlights-chip');

        const text = document.createElement('span');
        text.textContent = label;
        chip.append(text);

        if (sentiment === 'positive') chip.insertAdjacentHTML('beforeend', thumbUp);
        if (sentiment === 'negative') chip.insertAdjacentHTML('beforeend', thumbDown);
      });
    }

    placeTrigger() {
      if (!this.trigger) return;

      const badge =
        document.querySelector('.product__info-container .jdgm-preview-badge') ||
        document.querySelector('.product__info-container .jdgm-widget.jdgm-preview-badge');

      if (!badge) return;

      const host = badge.closest('.shopify-block') || badge;
      let row = host.parentElement;

      if (!row?.classList.contains('review-highlights-inline')) {
        row = document.createElement('div');
        row.className = 'review-highlights-inline';
        host.before(row);
        row.append(host);
      }

      if (this.trigger.parentElement !== row) {
        row.append(this.trigger);
      }
    }

    showTrigger() {
      if (!this.trigger) return;
      this.placeTrigger();
      this.trigger.hidden = false;
      this.trigger.classList.add('is-visible');
    }

    open() {
      this.attachInsights();
      if (!this.bodyEl.childElementCount) return;

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else if (!this.dialog.hasAttribute('open')) {
        this.dialog.setAttribute('open', '');
      }

      if (!this.supportsTopLayerAnimation) {
        requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      }

      document.documentElement.classList.add('review-highlights-drawer-open');
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-review-highlights-close]')?.focus();
    }

    close() {
      if (!this.dialog.open && !this.dialog.hasAttribute('open')) return;

      const finish = () => {
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.dialog.classList.remove('is-open');
          document.documentElement.classList.remove('review-highlights-drawer-open');
          this.trigger?.setAttribute('aria-expanded', 'false');
        }
      };

      if (this.supportsTopLayerAnimation || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }

      this.dialog.classList.remove('is-open');
      window.setTimeout(finish, 280);
    }
  }

  customElements.define('review-highlights-drawer', ReviewHighlightsDrawer);
}
